import { AlertTriangle, Building2, Calendar, CreditCard, DollarSign, Filter, Plus, Search } from 'lucide-react';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../../services/api';

const Payments = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // 결제 목록 가져오기
  const { data: payments, isLoading } = useQuery('payments', async () => {
    try {
      const response = await api.get('/payments');
      console.log('📊 Payments API 응답:', response);

      // 응답 데이터 구조 확인 및 안전한 처리
      let paymentsData = [];

      if (response && response.success && response.data) {
        // 표준 응답 구조: { success: true, data: [...] }
        paymentsData = response.data;
      } else if (response && Array.isArray(response)) {
        // 직접 배열 응답: [...]
        paymentsData = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        // response.data가 배열인 경우
        paymentsData = response.data;
      }

      console.log('✅ 최종 payments 데이터:', paymentsData);
      return paymentsData;
    } catch (error) {
      console.error('❌ Payments API 호출 실패:', error);
      return [];
    }
  });

  // 디버깅 로그 추가
  console.log('🔍 Payments 컴포넌트 디버깅:');
  console.log('  - payments 타입:', typeof payments);
  console.log('  - payments 값:', payments);
  console.log('  - payments가 배열인가?', Array.isArray(payments));
  console.log('  - payments 길이:', payments?.length);

  // 검색 및 필터링 (안전한 처리)
  const filteredPayments = (payments && Array.isArray(payments)) ? payments.filter(payment => {
    const matchesSearch = payment.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.building_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.unit_number?.includes(searchTerm);
    const matchesFilter = filterStatus === 'all' || payment.status === filterStatus;
    return matchesSearch && matchesFilter;
  }) : [];

  const handleSendPaymentConfirmedKakao = async (payment) => {
    if (!window.confirm('수납확정 알림톡을 발송 요청할까요?')) return;
    try {
      const response = await api.post(`/message-master/payments/${payment.id}/confirmed/send`, {});
      alert(`알림톡 요청 완료: ${response.status || response.data?.status || 'READY'}`);
    } catch (error) {
      alert(error.response?.data?.message || '알림톡 요청에 실패했습니다.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 페이지 제목 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">결제 관리</h1>
          <p className="mt-1 text-sm text-gray-500">
            임대료 및 관리비 결제를 관리하세요
          </p>
        </div>
        <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
          <Plus className="mr-2 h-4 w-4" />
          결제 등록
        </button>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* 검색 */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="임차인명, 건물명, 호수로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>

          {/* 상태 필터 */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="h-5 w-5 text-gray-400" />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 block w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">전체 상태</option>
              <option value="paid">납부완료</option>
              <option value="pending">납부대기</option>
              <option value="overdue">연체</option>
              <option value="partial">부분납부</option>
            </select>
          </div>

          {/* 통계 */}
          <div className="text-right">
            <p className="text-sm text-gray-500">총 {filteredPayments.length}건 결제</p>
          </div>
        </div>
      </div>

      {/* 결제 목록 */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredPayments.map((payment) => (
            <li key={payment.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <CreditCard className="h-6 w-6 text-primary-600" />
                      </div>
                      {['completed', 'paid'].includes(payment.status) && (
                        <button
                          onClick={() => handleSendPaymentConfirmedKakao(payment)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          알림톡
                        </button>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">{payment.tenant_name}</p>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${payment.status === 'paid' ? 'bg-green-100 text-green-800' :
                          payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            payment.status === 'overdue' ? 'bg-red-100 text-red-800' :
                              'bg-blue-100 text-blue-800'
                          }`}>
                          {payment.status === 'paid' ? '납부완료' :
                            payment.status === 'pending' ? '납부대기' :
                              payment.status === 'overdue' ? '연체' : '부분납부'}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <Building2 className="mr-1 h-4 w-4" />
                        {payment.building_name} - {payment.unit_number}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="mr-1 h-4 w-4" />
                        {new Date(payment.due_date).toLocaleDateString('ko-KR')}
                      </div>
                      <div className="flex items-center text-sm font-medium text-gray-900">
                        <DollarSign className="mr-1 h-4 w-4" />
                        ₩{payment.amount.toLocaleString()}
                      </div>
                      {payment.status === 'overdue' && (
                        <div className="flex items-center text-sm text-red-600">
                          <AlertTriangle className="mr-1 h-4 w-4" />
                          {payment.days_overdue}일 연체
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                        상세보기
                      </button>
                      <button className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                        수정
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* 빈 상태 */}
      {filteredPayments.length === 0 && (
        <div className="text-center py-12">
          <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {searchTerm || filterStatus !== 'all'
              ? '검색 조건에 맞는 결제 내역이 없습니다'
              : '결제 내역이 없습니다'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || filterStatus !== 'all'
              ? '다른 검색 조건을 시도해보세요.'
              : '현재 등록된 결제 내역이 없습니다. 청구서가 생성된 후 결제가 등록됩니다.'}
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <div className="mt-6">
              <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                <Plus className="mr-2 h-4 w-4" />
                결제 등록
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Payments;




